import { SHAPE_STYLES, TEXT_STYLE } from './constants';
import {
  addGameObject,
  anchoredPos,
  getObjectCountByType,
  getZeroStaticEntity,
  moveEntityToFront,
  setParent,
} from './entity';
import { createAndAddLocalContainer, setContainerStyle } from './container';
import { hasVisibleState, noAutoResize, v2 } from './helpers';
import {
  ServerEvent_EntityProps,
  ServerEvent_CmsConsole,
  JSON_CmsConsole,
  EventClientToServer,
  JSON_ServerDebugCommand,
  ObjectType,
} from './shared/api_shared_types';
import { setVisibilityState } from './simulate';
import { changeTextObjectText, createAndAddLocalTextObject } from './textobject/textobject';
import {
  Entity,
  VisibilityState,
  GameManager,
  MoveMode,
  CmsConsole,
  Shape,
  SharedObjectType,
  GameObjectController,
} from './types';

import { debugState } from './index';
import { createAndAddLocalTextButton } from './textbutton';
import logger from './controllers/logger';

export function controller(): GameObjectController {
  return {
    getNew: getZeroCmsConsole,
    sync,
    update,
  };
}

export function getZeroCmsConsole(ent: Entity) {
  let newCmsConsole: CmsConsole = {
    ...ent,
    objectType: SharedObjectType.CMS_CONSOLE,

    sequenceSlug: '',
    sequenceTitle: '',
    stepName: '',
    entityInfo: '',
    debugMsg: '',
    nextStepNames: [],
    sequenceState: '',

    reloadButton: null,
    reloadStepButton: null,
    skipLobbyButton: null,
    previousStepButton: null,
    clearButton: null,

    text: null,
    nextStepButtons: [],
  };

  return newCmsConsole;
}

function sync(manager: GameManager, cmsConsole: CmsConsole, data: ServerEvent_CmsConsole) {
  let entityProps: ServerEvent_EntityProps = {};

  if (data.hasOwnProperty('entityProps')) {
    entityProps = data.entityProps;
  }

  if (!cmsConsole.id) {
    addGameObject(manager, cmsConsole);
    initCmsConsole(manager, cmsConsole);

    // setParentByEntityProps(manager, cmsConsole, data.sId,  data.roomId, entityProps);
    setParent(cmsConsole, manager.mainRoom.lobbyLayer, v2(400, 400));
    moveEntityToFront(cmsConsole);
  }

  if (data.hasOwnProperty('sequenceSlug')) {
    cmsConsole.sequenceSlug = data.sequenceSlug;
  }

  if (data.hasOwnProperty('sequenceTitle')) {
    cmsConsole.sequenceTitle = data.sequenceTitle;
  }

  if (data.hasOwnProperty('stepName')) {
    cmsConsole.stepName = data.stepName;
  }

  if (data.hasOwnProperty('sequenceState')) {
    cmsConsole.sequenceState = data.sequenceState;
  }

  if (data.hasOwnProperty('debugMsg')) {
    cmsConsole.debugMsg = data.debugMsg;
  }

  if (data.hasOwnProperty('roomDataValues')) {
    if (cmsConsole.visibilityState === VisibilityState.DEFAULT) {
      console.table(data.roomDataValues);
    }
  }

  updateData(cmsConsole);

  if (data.hasOwnProperty('nextStepNames')) {
    cmsConsole.nextStepNames = data.nextStepNames;
    updateNextStepButtons(cmsConsole);
  }

  updateLayout(cmsConsole);
}

function initCmsConsole(manager: GameManager, cmsConsole: CmsConsole) {
  cmsConsole.shape = Shape.RECT;

  cmsConsole.moveMode = MoveMode.STATIC;
  cmsConsole.influenceKinetic = false;
  cmsConsole.allowDrag = true;
  cmsConsole.allowHighlight = true;

  cmsConsole.height = 200;
  cmsConsole.width = 420;

  const bg = createAndAddLocalContainer(manager, getZeroStaticEntity());
  bg.width = cmsConsole.width;
  bg.height = cmsConsole.height;
  bg.relativeWidth = 1;
  bg.relativeHeight = 1;
  setContainerStyle(bg, SHAPE_STYLES.DEBUG);

  setParent(bg, cmsConsole, v2());

  cmsConsole.text = createAndAddLocalTextObject(
    manager,
    getZeroStaticEntity(),
    `CMS Console`,
    TEXT_STYLE.CMS_CONSOLE_TEXT,
    3,
  );

  cmsConsole.text.moveMode = MoveMode.ANCHORED;
  cmsConsole.text.anchoredPos = anchoredPos(v2(0, 0), v2(10, 10), v2(0, 0));
  setParent(cmsConsole.text, cmsConsole);

  {
    cmsConsole.reloadButton = createAndAddLocalTextButton(
      manager,
      getZeroStaticEntity({ disableAutoSize: true }),
      'Reload',
      '',
      TEXT_STYLE.CMS_CONSOLE_BUTTON,
      0.2,
    );

    cmsConsole.reloadButton.moveMode = MoveMode.ANCHORED;

    cmsConsole.reloadButton.anchoredPos = anchoredPos(v2(1, 0), v2(-10, 10), v2(1, 0));
    setParent(cmsConsole.reloadButton, cmsConsole);
    setVisibilityState(cmsConsole.reloadButton, VisibilityState.APPEARING);
  }

  {
    cmsConsole.clearButton = createAndAddLocalTextButton(
      manager,
      getZeroStaticEntity({ disableAutoSize: true }),
      'Clear room',
      '',
      TEXT_STYLE.CMS_CONSOLE_BUTTON,
      0.2,
    );

    cmsConsole.clearButton.moveMode = MoveMode.ANCHORED;

    cmsConsole.clearButton.anchoredPos = anchoredPos(v2(1, 0), v2(-10, 40), v2(1, 0));
    setParent(cmsConsole.clearButton, cmsConsole);
    setVisibilityState(cmsConsole.clearButton, VisibilityState.APPEARING);
  }
  {
    cmsConsole.skipLobbyButton = createAndAddLocalTextButton(
      manager,
      getZeroStaticEntity({ disableAutoSize: true }),
      'Skip lobby',
      '',
      TEXT_STYLE.CMS_CONSOLE_BUTTON,
      0.2,
    );

    cmsConsole.skipLobbyButton.moveMode = MoveMode.ANCHORED;

    cmsConsole.skipLobbyButton.anchoredPos = anchoredPos(v2(1, 0), v2(-10, 70), v2(1, 0));
    setParent(cmsConsole.skipLobbyButton, cmsConsole);
    // setEntityState(cmsConsole.skipLobbyButton, EntityState.APPEARING);
  }

  {
    cmsConsole.previousStepButton = createAndAddLocalTextButton(
      manager,
      getZeroStaticEntity({ disableAutoSize: true }),
      'Prev step',
      'iconPageBack',
      noAutoResize(TEXT_STYLE.CMS_CONSOLE_BUTTON),
      0.2,
    );

    cmsConsole.previousStepButton.moveMode = MoveMode.ANCHORED;
    cmsConsole.previousStepButton.anchoredPos = anchoredPos(v2(0, 0), v2(10, 10), v2(0, 0));
    setParent(cmsConsole.previousStepButton, cmsConsole);
    setVisibilityState(cmsConsole.previousStepButton, VisibilityState.APPEARING);
  }

  {
    cmsConsole.reloadStepButton = createAndAddLocalTextButton(
      manager,
      getZeroStaticEntity({ disableAutoSize: true }),
      'Reload Step',
      'iconReload',
      noAutoResize(TEXT_STYLE.CMS_CONSOLE_BUTTON),
      0.2,
    );

    cmsConsole.reloadStepButton.moveMode = MoveMode.ANCHORED;
    cmsConsole.reloadStepButton.anchoredPos = anchoredPos(v2(0, 0), v2(140, 10), v2(0, 0));
    setParent(cmsConsole.reloadStepButton, cmsConsole);
    setVisibilityState(cmsConsole.reloadStepButton, VisibilityState.APPEARING);
  }

  if (debugState.showCmsConsole) {
    setVisibilityState(cmsConsole, VisibilityState.APPEARING);
  }
}

function updateData(cmsConsole: CmsConsole) {
  let text = `Sequence Slug: <b>${cmsConsole.sequenceSlug}</b>\n`;
  text += `Sequence Title: <b>${cmsConsole.sequenceTitle}</b>\n`;
  text += `Step name: <b>${cmsConsole.stepName}</b>\n`;
  text += cmsConsole.sequenceState !== 'ACTIVE' ? `<b>${cmsConsole.sequenceState}</b>\n` : '';
  text += cmsConsole.debugMsg ? `Warning: <b>${cmsConsole.debugMsg}</b>\n` : '';
  changeTextObjectText(cmsConsole.text, text);
}

function updateNextStepButtons(cmsConsole: CmsConsole) {
  for (let i = 0; i < cmsConsole.nextStepButtons.length; i++) {
    setVisibilityState(cmsConsole.nextStepButtons[i], VisibilityState.DISAPPEARING);
  }

  cmsConsole.nextStepButtons.length = 0;

  let y = cmsConsole.text.height + 90;

  for (let i = 0; i < cmsConsole.nextStepNames.length; i++) {
    const label = cmsConsole.nextStepNames[i];
    const btn = createAndAddLocalTextButton(
      cmsConsole.manager,
      getZeroStaticEntity({ disableAutoSize: true }),
      label,
      'buttonContinue',
      TEXT_STYLE.CMS_CONSOLE_BUTTON,
      0.3,
    );

    btn.showLoader = true;

    setParent(btn, cmsConsole, v2());
    btn.moveMode = MoveMode.ANCHORED;
    // const startX = cmsConsole.text.width + 20;

    // const x = startX + Math.floor(i / 3) * 300;
    btn.anchoredPos = anchoredPos(v2(0, 0), v2(10, y), v2(0, 0));
    y += btn.height + 10;
    cmsConsole.nextStepButtons.push(btn);

    setVisibilityState(btn, VisibilityState.APPEARING);
  }

  cmsConsole.height = y + 10;
  cmsConsole.layoutChanged = true;
}

function update(obj: CmsConsole): boolean {
  // The lobby game object was removed, so the "skip lobby" button is no longer
  // relevant — keep it hidden.
  if (hasVisibleState(obj.skipLobbyButton.visibilityState)) {
    setVisibilityState(obj.skipLobbyButton, VisibilityState.HIDING);
  }

  return false;
}

function updateLayout(cmsConsole: CmsConsole) {
  let y = cmsConsole.text.height + 50;

  cmsConsole.previousStepButton.anchoredPos.offset.y = y;
  cmsConsole.reloadStepButton.anchoredPos.offset.y = y;

  y += cmsConsole.reloadButton.height + 10;

  y += cmsConsole.reloadStepButton.height + 10;
}
